const TWO_PART_ACTION_SUFFIXES = [
  'permanent-delete',
  'bulk-delete',
  'recreate-installment-token',
];

const SINGLE_ACTION_SUFFIXES = new Set([
  'list',
  'create',
  'view',
  'edit',
  'delete',
  'restore',
  'export',
  'download',
  'is_approved',
]);

/**
 * Module slug from permission name, e.g. task-type-list → task-type, invoice-edit → invoice.
 */
export function getPermissionModule(slug) {
  const name = String(slug || '').trim();
  if (!name) return 'general';

  for (const suffix of TWO_PART_ACTION_SUFFIXES) {
    if (name.endsWith(`-${suffix}`)) {
      return name.slice(0, -(suffix.length + 1)) || 'general';
    }
  }

  const parts = name.split('-');
  const last = parts[parts.length - 1];
  if (SINGLE_ACTION_SUFFIXES.has(last) && parts.length > 1) {
    return parts.slice(0, -1).join('-');
  }

  return name;
}

/**
 * Parent module family, e.g. task-type → task, payment-gateway → payment.
 */
export function getModuleFamily(module) {
  const mod = String(module || '').trim();
  if (!mod || mod === 'general') return 'general';

  if (mod === 'task' || mod.startsWith('task-')) return 'task';
  if (mod === 'payment' || mod.startsWith('payment-')) return 'payment';
  if (mod === 'lead' || mod.startsWith('lead-')) return 'lead';
  if (mod === 'invoice' || mod.startsWith('invoice-')) return 'invoice';
  if (mod === 'brand' || mod.startsWith('brand')) return 'brand';
  if (mod === 'service' || mod.startsWith('service')) return 'service';
  if (mod === 'company' || mod.startsWith('company')) return 'company';
  if (mod === 'llc' || mod.startsWith('llc-')) return 'llc';

  return mod;
}

export function formatPermissionGroupLabel(key) {
  return String(key || '')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * @param {Array<{ id: number|string, name: string }>} permissions
 * @returns {Record<string, Record<string, typeof permissions>>}
 */
export function groupPermissionsByFamily(permissions) {
  const families = {};

  permissions.forEach((permission) => {
    const module = getPermissionModule(permission.name);
    const family = getModuleFamily(module);

    if (!families[family]) {
      families[family] = {};
    }
    if (!families[family][module]) {
      families[family][module] = [];
    }
    families[family][module].push(permission);
  });

  Object.keys(families).forEach((family) => {
    Object.keys(families[family]).forEach((module) => {
      families[family][module].sort((a, b) =>
        String(a.name).localeCompare(String(b.name))
      );
    });
  });

  return families;
}

export function getIdsFromPermissionList(permissions) {
  return (permissions ?? []).map((p) => p.id);
}

export function getFamilyPermissionIds(family, groupedByFamily) {
  const modules = groupedByFamily[family] ?? {};
  return Object.values(modules).flat().map((p) => p.id);
}

export function getModulePermissionIds(module, modulesMap) {
  return getIdsFromPermissionList(modulesMap[module]);
}
